import { useQuery } from 'convex/react';
import { api } from '@bill-alps/backend/convex/_generated/api';
import {
  LayoutDashboard,
  FileText,
  FileSignature,
  Users,
  Building2,
  Layers,
  UserCircle,
  MessageCircle,
  ChevronsLeft,
  UserCheck,
  Receipt,
  ReceiptText,
  Repeat,
  Package,
  ListOrdered,
  Scale,
  Database,
  ChartLine,
  ShieldCheck,
  BriefcaseBusiness,
  type LucideIcon,
  BookOpen,
  BookText,
  Calculator,
  CalendarRange,
  FileCheck2,
  Landmark,
} from 'lucide-react';
import { Button, cn, Separator } from '@bill-alps/ui';
import { isModuleEnabled } from '@bill-alps/backend/features/companies/modules';
import { isCatalogEnabled } from '@bill-alps/backend/features/companies/catalog';
import { useTranslation } from '../../i18n/useTranslation';
import { useCurrentUser } from '@/hooks/use-current-user';
import {
  LEDGER_AVAILABLE,
  MODULE_AVAILABLE,
  STATEMENT_REPORTING_AVAILABLE,
} from '@/lib/runtime/feature-flags';
import { SidebarNavItem } from './sidebar-nav-item';
import { SidebarNavGroup } from './sidebar-nav-group';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
  className?: string;
  isAdmin?: boolean;
}

const billingCategory = {
  labelKey: 'nav.categoryBilling',
  items: [
    { to: '/quotes', icon: FileSignature, labelKey: 'nav.quotes' },
    { to: '/bills', icon: FileText, labelKey: 'nav.bills' },
    {
      to: '/recurring-invoices',
      icon: Repeat,
      labelKey: 'nav.recurringInvoices',
    },
    { to: '/clients', icon: Users, labelKey: 'nav.clients' },
    { to: '/templates', icon: Layers, labelKey: 'nav.templates' },
  ],
} as const;

// Reusable priced lines, one module away from the documents that consume
// them: same `invoicing` gate as the billing category above.
const catalogCategory = {
  labelKey: 'nav.categoryCatalog',
  items: [
    { to: '/products', icon: Package, labelKey: 'nav.products' },
    { to: '/product-lists', icon: ListOrdered, labelKey: 'nav.productLists' },
  ],
} as const;

// Both items belong to the single `accounting` module: the category appears
// and disappears as a whole with it.
const accountingCategory = {
  labelKey: 'nav.categoryAccounting',
  items: [
    { to: '/expenses', icon: ReceiptText, labelKey: 'nav.expenses' },
    // Prepares documents for the ledger: nothing comes of it while that is dark.
    ...(LEDGER_AVAILABLE
      ? [
          {
            to: '/accounting/to-classify',
            icon: BookOpen,
            labelKey: 'nav.toClassify',
          },
        ]
      : []),
    {
      to: '/accounting/bank',
      icon: Scale,
      labelKey: 'nav.bankTransactions',
    },
    // Ledger surfaces ride the same module, behind their own kill switch.
    ...(LEDGER_AVAILABLE
      ? [
          {
            to: '/accounting/journal',
            icon: BookText,
            labelKey: 'nav.journal',
          },
          {
            to: '/accounting/chart',
            icon: BookOpen,
            labelKey: 'nav.chartOfAccounts',
          },
          {
            to: '/accounting/vat',
            icon: Calculator,
            labelKey: 'nav.vatReturns',
          },
          {
            to: '/accounting/fiscal-years',
            icon: CalendarRange,
            labelKey: 'nav.fiscalYears',
          },
          {
            to: '/accounting/fixed-assets',
            icon: Landmark,
            labelKey: 'nav.fixedAssets',
          },
          ...(STATEMENT_REPORTING_AVAILABLE
            ? [
                {
                  to: '/accounting/annual-accounts',
                  icon: FileCheck2,
                  labelKey: 'nav.annualAccounts',
                },
              ]
            : []),
        ]
      : []),
  ],
} as const;

const salaryCategory = {
  labelKey: 'nav.categorySalary',
  items: [
    { to: '/employees', icon: UserCheck, labelKey: 'nav.employees' },
    { to: '/payslips', icon: Receipt, labelKey: 'nav.payslips' },
  ],
} as const;

const settingsCategory = {
  labelKey: 'nav.categorySettings',
  items: [
    { to: '/company', icon: Building2, labelKey: 'nav.company' },
    { to: '/account', icon: UserCircle, labelKey: 'nav.account' },
  ],
} as const;

export function Sidebar({
  isCollapsed,
  onToggle,
  onNavigate,
  className,
  isAdmin,
}: SidebarProps) {
  const { t } = useTranslation('common');
  const {
    user,
    canReadModule,
    canWriteModule,
    isAccountAdmin,
    isMandateSelected,
  } = useCurrentUser();
  const invoicingEnabled =
    MODULE_AVAILABLE.invoicing &&
    isModuleEnabled(user?.company, 'invoicing') &&
    canReadModule('invoicing');
  const payrollEnabled =
    MODULE_AVAILABLE.payroll &&
    isModuleEnabled(user?.company, 'payroll') &&
    canReadModule('payroll');
  const catalogEnabled = invoicingEnabled && isCatalogEnabled(user?.company);
  const accountingEnabled =
    MODULE_AVAILABLE.accounting &&
    isModuleEnabled(user?.company, 'accounting') &&
    canReadModule('accounting');

  const ledger = useQuery(
    api.features.ledger.settings.get,
    LEDGER_AVAILABLE && accountingEnabled && user?.company
      ? { companyId: user.company._id }
      : 'skip',
  );
  const ledgerActive =
    ledger?.settings?.status !== 'importing' &&
    ledger?.settings?.activatedAt !== undefined;
  const ledgerNavigation = {
    ...accountingCategory,
    items: accountingCategory.items.filter(
      (item) =>
        ledgerActive ||
        canWriteModule('accounting') ||
        ['/expenses', '/accounting/bank', '/accounting/chart'].includes(
          item.to,
        ),
    ),
  };

  // On a mandate the trustee works for a client: the company page wears the
  // mandate's name and the personal account/billing page disappears.
  const settingsItems = isMandateSelected
    ? [
        {
          to: '/company',
          icon: Building2,
          label: user?.company?.name ?? t('nav.company'),
        },
      ]
    : settingsCategory.items
        .filter((item) => isAccountAdmin || item.to === '/account')
        .map((item) => ({
          to: item.to,
          icon: item.icon,
          label: t(item.labelKey),
        }));

  const categories: {
    labelKey: string;
    items: { to: string; icon: LucideIcon; label: string }[];
  }[] = [
    ...[
      ...(invoicingEnabled ? [billingCategory] : []),
      ...(catalogEnabled ? [catalogCategory] : []),
      ...(accountingEnabled ? [ledgerNavigation] : []),
      ...(payrollEnabled ? [salaryCategory] : []),
    ].map(({ labelKey, items }) => ({
      labelKey,
      items: items.map((item) => ({
        to: item.to,
        icon: item.icon,
        label: t(item.labelKey),
      })),
    })),
    { labelKey: 'nav.categorySettings', items: settingsItems },
    // Admin — visible only for @w-ap.ch users. Listed as a category rather than
    // hand-rolled below so it wears the same header and folds like the rest.
    ...(isAdmin
      ? [
          {
            labelKey: 'nav.categoryAdmin',
            items: [
              { to: '/admin/data', icon: Database, label: t('nav.adminData') },
              {
                to: '/admin/stats',
                icon: ChartLine,
                label: t('nav.adminStats'),
              },
              {
                to: '/admin/compliance',
                icon: ShieldCheck,
                label: t('nav.adminCompliance'),
              },
            ],
          },
        ]
      : []),
  ];

  return (
    <aside
      className={cn(
        'sticky top-16 flex h-[calc(var(--app-viewport-svh)_-_4rem)] flex-col border-r-2 border-border bg-card transition-[width] duration-300 ease-spring overflow-hidden',
        isCollapsed ? 'w-16' : 'w-60',
        className,
      )}
    >
      <div className="shrink-0">
        <Button
          type="button"
          variant="link"
          hoverEffect="none"
          onClick={onToggle}
          aria-label={isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          title={isCollapsed ? t('sidebar.expand') : undefined}
          className={cn(
            'h-14 w-full shrink-0 justify-start rounded-none border-0 px-3 py-2.5 text-base font-normal text-muted-foreground no-underline shadow-none hover:bg-muted/50 hover:text-foreground hover:no-underline focus-visible:ring-inset',
            isCollapsed && 'justify-center',
          )}
        >
          <ChevronsLeft
            data-sidebar-toggle-icon
            aria-hidden="true"
            className={cn(
              'size-4 transition-transform duration-300 ease-spring',
              isCollapsed && 'rotate-180',
            )}
          />
          {!isCollapsed && <span>{t('sidebar.collapse')}</span>}
        </Button>
        <Separator
          data-sidebar-toggle-separator
          className="border-t border-solid border-border"
        />
      </div>

      <nav className="flex min-h-0 flex-1 flex-col">
        {/* The navigation list is the sidebar's only scrollable region. */}
        <div
          data-sidebar-navigation
          className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 py-4 scrollbar-hide"
        >
          {/* Dashboard — standalone */}
          <SidebarNavItem
            to="/dashboard"
            icon={LayoutDashboard}
            label={t('nav.dashboard')}
            isCollapsed={isCollapsed}
            onNavigate={onNavigate}
            className="mb-2"
          />

          {user?.fiduciaire && (
            <SidebarNavItem
              to="/mandates"
              icon={BriefcaseBusiness}
              label={t('nav.mandates')}
              isCollapsed={isCollapsed}
              onNavigate={onNavigate}
              className="mb-2"
            />
          )}

          {/* Categorized navigation — each header folds its own items away */}
          {categories.map(({ labelKey, items }) => (
            <SidebarNavGroup
              key={labelKey}
              groupKey={labelKey}
              label={t(labelKey)}
              items={items}
              isCollapsed={isCollapsed}
              onNavigate={onNavigate}
            />
          ))}
        </div>

        {/* Support stays reachable while the navigation list scrolls. */}
        <div data-sidebar-support className="shrink-0 px-2 pb-4">
          <Separator
            data-sidebar-support-separator
            className="mb-4 border-border"
          />
          <SidebarNavItem
            to="/support"
            icon={MessageCircle}
            label={t('nav.support')}
            isCollapsed={isCollapsed}
            onNavigate={onNavigate}
          />
        </div>
      </nav>
    </aside>
  );
}
